import { useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../shell/AppShell.jsx";
import DetailDrawer from "../components/DetailDrawer.jsx";
import { useBackofficeIdentity } from "./BackofficeIdentityContext.jsx";
import { useBackofficeDashboardData, HIERARCHY_FEED_KEY } from "./useBackofficeDashboardData.js";
import {
  ChartPanel,
  AxisBarChart,
  DonutChart,
  FunnelChart,
  HierarchyFlow,
  TrendShell,
  resolveBar,
  formatCount,
} from "./DashboardCharts.jsx";
import { findOwningAgentId } from "../agent/ownership.js";
import { useOwningAgentProfile } from "../agent/useOwningAgentProfile.js";
import { useAgentApplicationsCount } from "../agent/useAgentApplicationsCount.js";
import { useAgentApplicantDetail } from "../agent/useAgentApplicantDetail.js";
import { ApplicantDashboardItem } from "../agent/ApplicantCard.jsx";
import { ApplicantDetailContent } from "../agent/ApplicantDetailDrawer.jsx";
import "./home.css";
import "../pages/states.css";
import { isJobDemandSide } from "../workerLine/workerLineShared.js";

import PageSkeleton from "../components/Skeleton.jsx";
const APPLICANT_DRAWER_TITLE_ID = "dash-applicant-drawer-title";
const APPLICANT_PIPELINE_PAGE_SIZE = 20;

const SUPERADMIN_QUICK_ACTIONS = [
  { label: "Open War Room", to: "/backoffice/war-room" },
  { label: "Review e-KYC Queue", to: "/backoffice/worker-intake/ekyc-review" },
  { label: "Review SOS Cases", to: "/backoffice/sos/cases" },
  { label: "Review Fake Location Signals", to: "/backoffice/tracking/fake-location" },
  { label: "Review Matching Queue", to: "/backoffice/thailand-jobs/matching-queue" },
  { label: "Open Worker Line Org Chart", to: "/backoffice/worker-line/org-chart" },
];

// Every target here is an existing, already-implemented Agent Portal or
// Thailand Jobs screen - never a placeholder. "Employers / Job Demand"
// points at the shared (non-Agent-prefixed) Thailand Jobs Employers screen
// because that is the one real route this data lives behind; an Agent
// session there is scope-limited server-side to their own line exactly like
// every other read on this page.
// P156: the job side's own list. Not AGENT_QUICK_ACTIONS with entries hidden -
// four of its six are worker-only, so what is left would not be a useful list.
const AGENT_JOB_SIDE_QUICK_ACTIONS = [
  { label: "Invite Card นายจ้าง", to: "/backoffice/agent/employer-invite-cards" },
  // The separate "Jobs" chip went away on 2026-08-05 (owner): a job lives
  // inside its employer, and two chips for one thing was the confusion being
  // removed. "Employers / Job Demand" above is that one entry point.
  { label: "Employers / Job Demand", to: "/backoffice/thailand-jobs/employers" },
  { label: "Org Chart", to: "/backoffice/agent/org-chart" },
];

const AGENT_QUICK_ACTIONS = [
  { label: "Invite Card", to: "/backoffice/agent/invite-cards" },
  { label: "Workers", to: "/backoffice/agent/workers" },
  { label: "Employers / Job Demand", to: "/backoffice/thailand-jobs/employers" },
  { label: "SOS / Abnormal", to: "/backoffice/agent/sos-abnormal" },
  { label: "Footstep", to: "/backoffice/agent/footsteps" },
  { label: "Zones", to: "/backoffice/agent/zones" },
];

const FEED_STATE_LABELS = {
  loading: "กำลังโหลด...",
  loaded: "โหลดสำเร็จ",
  "no-data": "ไม่มีข้อมูล",
  failed: "โหลดไม่สำเร็จ",
};

/** Same th-TH/ICT formatting convention WarRoomPage.jsx's own formatDateTimeTh uses - duplicated locally rather than shared across features. */
function formatTimeTh(date) {
  const datePart = date.toLocaleDateString("th-TH-u-ca-gregory", { day: "2-digit", month: "short" });
  const timePart = date.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${datePart} · ${timePart} ICT`;
}

/**
 * The real /backoffice landing page after login, and the single role-aware
 * dashboard for both SuperAdmin and Agent sessions - there is no separate
 * "simple" Agent dashboard page. Every feed is fetched exactly once,
 * unconditionally, through the same useBackofficeDashboardData() hook for
 * both roles: every underlying endpoint already scopes its own result set
 * server-side from the caller's own AccessScopes (SeesAll only for a
 * Platform scope, otherwise narrowed to the caller's own Agent/Plc/
 * SuperAgent branch - see each service's own ResolveScope), so an Agent
 * session sees only their own branch's numbers with zero Agent-specific
 * fetch logic here. Only the page's copy and the Quick Actions list are
 * role-conditional - never the data-fetching shape, and never the layout.
 *
 * P144-R rebuilt the presentation layer from scratch (not patched) against
 * the LiveMall Front executive dashboard reference: a dense two-band KPI
 * strip with real risk tinting, then a multi-column analytics grid where
 * charts sit side by side instead of stacking full-width, then a compact
 * operational rail. Every number still comes from the same real endpoints
 * that were already wired - no new backend call, no fabricated series.
 */
export default function BackofficeHomePage() {
  const { status, data, error } = useBackofficeIdentity();
  const isAgent = status === "ready" && data.primaryRole === "Agent";
  const crumbs = ["Backoffice", isAgent ? "Agent" : "SuperAdmin", "Dashboard"];

  if (status === "loading") {
    return (
      <AppShell crumbs={crumbs}>
        <PageSkeleton variant="dashboard" label="กำลังโหลดข้อมูลผู้ใช้งาน..." />
      </AppShell>
    );
  }

  if (status === "error") {
    return (
      <AppShell crumbs={crumbs}>
        <div className="wx-state-card wx-state-card--danger" role="alert">
          <h1 className="wx-state-title">ไม่สามารถโหลดหน้าหลักได้</h1>
          <p className="wx-state-body">
            {error?.status === 401
              ? "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่"
              : "กรุณาลองรีเฟรชหน้านี้อีกครั้ง หากยังพบปัญหากรุณาติดต่อฝ่ายปฏิบัติการ"}
          </p>
        </div>
      </AppShell>
    );
  }

  return <DashboardBody data={data} crumbs={crumbs} isAgent={isAgent} />;
}

function DashboardBody({ data, crumbs, isAgent }) {
  // P156: a job-side Agent (ฝั่งงาน) holds employers, never workers, so every
  // worker-safety surface below is removed rather than shown reading zero.
  const isJobSideAgent = isAgent && isJobDemandSide(data?.organizationSide);
  const feeds = useBackofficeDashboardData();
  const agentId = isAgent ? findOwningAgentId(data.accessScopes) : null;
  const agentProfile = useOwningAgentProfile(agentId);
  const applicantPipeline = useAgentApplicationsCount(isAgent, { pageSize: APPLICANT_PIPELINE_PAGE_SIZE });
  // Prompt 140B-R3: a second, lightweight exact-match count purely to
  // subtract out applications that already became a real Worker Profile -
  // see workerMetricFeed's own doc comment below for why this second call
  // (not a client-side filter of applicantPipeline.items) is required.
  const workerProfileCreatedApplications = useAgentApplicationsCount(isAgent, { pageSize: 1, status: "WorkerProfileCreated" });
  const [selectedApplicationId, setSelectedApplicationId] = useState(null);
  const applicantDetailState = useAgentApplicantDetail(selectedApplicationId);
  // A real client timestamp of the moment this dashboard session actually
  // asked the server for data (component mount, captured once) - never a
  // fabricated "last synced" claim from a backend that has no such field.
  const [updatedAt] = useState(() => new Date());
  const { summary } = data;
  const readyFeed = (value) => ({ status: "ready", totalCount: value });

  // Prompt 140B-R2: a person who Quick Registered through this Agent's own
  // invite link is already a real worker in their line the moment they
  // exist - e-KYC/Worker Profile completion are statuses on that same real
  // person, never a condition for whether they count as a worker at all.
  // The backend's own summary.visibleWorkerCount only ever counts real
  // dbo.WmxWorkers rows (BackofficeHierarchyService.GetHomeAsync ->
  // CountWorkersByAgentIdsAsync("dbo.WmxWorkers", ...)).
  //
  // Prompt 140B-R3: R2's own fix above added applicantPipeline.totalCount
  // (the RAW, unfiltered GET /api/backoffice/worker-applications count,
  // every status included) on top of summary.visibleWorkerCount directly -
  // but a real WmxWorkers row is only ever created once an application's
  // own ApplicationStatus reaches WorkerProfileCreated (Prompt 097), and
  // that application row is never deleted afterwards. So every
  // WorkerProfileCreated application was already double-counted: once in
  // visibleWorkerCount (their own real Worker Profile), once again in the
  // raw applicantPipeline.totalCount (their own still-existing application
  // row). Fixed by subtracting the real WorkerProfileCreated sub-count
  // (workerProfileCreatedApplications.totalCount, one more exact-match
  // GET .../worker-applications?status=WorkerProfileCreated&pageSize=1
  // call - the P093 contract only supports a single exact-match `status`,
  // never a "not equal"/"in" filter, so this is the honest way to derive
  // "applications not yet promoted to a profile" from two real numbers
  // rather than one unfiltered one) before adding - a real sum of three
  // real numbers, never a fabricated one. `Math.max(0, ...)` only guards
  // against transient skew between two independently-timed real counts,
  // never papering over a real negative. SuperAdmin's platform-wide
  // WORKERS tile is intentionally left unchanged (out of this fix's scope).
  const workerCountsReady = applicantPipeline.status === "ready" && workerProfileCreatedApplications.status === "ready";
  const workerCountsErrored = applicantPipeline.status === "error" || workerProfileCreatedApplications.status === "error";
  const workerMetricFeed = isAgent
    ? workerCountsReady
      ? {
          status: "ready",
          totalCount: summary.visibleWorkerCount + Math.max(0, applicantPipeline.totalCount - workerProfileCreatedApplications.totalCount),
        }
      : { status: workerCountsErrored ? "error" : "loading" }
    : readyFeed(summary.visibleWorkerCount);

  const heading = isAgent ? "Agent Dashboard" : "SuperAdmin Dashboard";
  const subtitle = isAgent
    ? "ภาพรวมสายงานของคุณ: Invite Card, แรงงาน, นายจ้าง และความปลอดภัย อ้างอิงจากข้อมูลจริงของบัญชีคุณเท่านั้น"
    : "ภาพรวม platform สำหรับแรงงานหลักแสนคน, safety monitoring, agent SLA, job operations และ compliance.";
  const scopeLabel = isAgent
    ? agentProfile.status === "ready" && agentProfile.data
      ? `Agent · ${agentProfile.data.agentCode}`
      : "Agent · สายงานของคุณ"
    : "SuperAdmin · ทุกสายงาน";

  // The primary CTA follows whichever risk signal is actually live right
  // now, rather than being a fixed link: a real open SOS case outranks a
  // missing heartbeat, and with neither open the operator is sent to the
  // full War Room board instead of an empty queue.
  const primaryAction = resolvePrimaryAction(feeds, isAgent, isJobSideAgent);

  return (
    <AppShell crumbs={crumbs}>
      <div className="wx-dash">
        <header className="wx-dash-head">
          <div className="wx-dash-head-text">
            <h1 className="wx-dash-greeting">{heading}</h1>
            <p className="wx-dash-subtitle">{subtitle}</p>
          </div>
          <div className="wx-dash-head-right">
            <span className="wx-dash-clock">
              <span className="wx-dash-clock-dot" aria-hidden="true" />
              อัปเดตล่าสุด {formatTimeTh(updatedAt)}
            </span>
            <span className="wx-dash-scope-chip">{scopeLabel}</span>
            <label className="wx-dash-search">
              <span className="wx-visually-hidden">Search worker, agent, employer, passport, phone</span>
              <input type="search" placeholder="ค้นหาแรงงาน, agent, นายจ้าง..." disabled title="ฟังก์ชันค้นหายังไม่เปิดใช้งาน" />
            </label>
            {primaryAction && (
              <Link to={primaryAction.to} className="wx-button wx-button--primary wx-dash-cta">
                {primaryAction.label}
              </Link>
            )}
          </div>
        </header>

        {/* Two dense KPI bands. Band 1 is the neutral scale of the operation
            (people, partners, demand); band 2 is everything that can carry
            real risk, so red/amber tinting is concentrated in one place and
            reads instantly instead of being scattered. */}
        <div className="wx-dash-kpis">
        <div className="wx-dash-metrics">
          <MetricCard label="WORKERS" feed={workerMetricFeed} note={isAgent ? "รวม Worker Profile และ Quick Registered" : undefined} />
          <MetricCard label="AGENTS" feed={readyFeed(summary.visibleAgentCount)} to="/backoffice/super-agents" />
          <MetricCard label="EMPLOYERS" feed={readyFeed(summary.visibleEmployerCount)} to="/backoffice/thailand-jobs/employers" />
          <MetricCard label="OPEN JOBS" feed={readyFeed(summary.visibleJobCount)} to="/backoffice/thailand-jobs/jobs" />
          <MetricCard
            label="QUICK REGISTERED"
            feed={feeds.applications}
            note="ยังไม่เข้าสู่ e-KYC"
            to={isAgent ? "/backoffice/agent/applications" : "/backoffice/worker-intake/invite-applications"}
          />
          <MetricCard label="WORKER PROFILES" feed={feeds.workerProfiles} to="/backoffice/worker-intake/worker-profiles" />
        </div>

        <div className="wx-dash-metrics wx-dash-metrics--risk">
          <MetricCard
            label="READY FOR MATCH"
            feed={feeds.readyForMatchQueue}
            healthyAccent
            note="พร้อมจับคู่งาน"
            to="/backoffice/worker-intake/ready-for-match"
          />
          <MetricCard
            label="E-KYC PENDING REVIEW"
            feed={feeds.ekyc}
            valueSelector={(f) => f.summary?.pendingReviewCount ?? f.totalCount}
            note="รอการตัดสินใจ (Submitted)"
            attentionAccent="warning"
            to="/backoffice/worker-intake/ekyc-review"
          />
          {/* P156: the risk band is worker tracking end to end - SOS, missing
              heartbeat, face check. On a job-side line every one of them is
              structurally zero, and a row of red-accented zeroes reads as a
              healthy safety record rather than as "not applicable here". */}
          {!isJobSideAgent && (
            <>
              <MetricCard label="SOS OPEN CASES" feed={feeds.sosOpen} attentionAccent="danger" to="/backoffice/sos/cases" />
              <MetricCard label="ABNORMAL OPEN EVENTS" feed={feeds.abnormalOpen} attentionAccent="danger" to="/backoffice/abnormal/heartbeat" />
              <MetricCard label="MISSING HEARTBEAT" feed={feeds.heartbeatMissing} attentionAccent="warning" to="/backoffice/tracking/latest" />
              <MetricCard label="FACE CHECK ATTENTION" feed={feeds.faceCheckAttention} attentionAccent="warning" to="/backoffice/face-check/daily" />
            </>
          )}
        </div>
        </div>

        <div className="wx-dash-grid">
          <div className="wx-dash-main-col">
            {/* Analytics rows pair a wide chart with a narrow one, the
                LiveMall 2fr/1fr rhythm - so the fold shows several real
                charts at once instead of one full-width panel per screen. */}
            <div className="wx-dash-row wx-dash-row--wide-narrow">
              <OperationsSnapshotPanel feeds={feeds} />
              <ComplianceDistributionPanel feed={feeds.ekyc} />
            </div>

            <div className="wx-dash-row wx-dash-row--wide-narrow">
              <WorkerIntakeFunnelPanel feeds={feeds} isAgent={isAgent} />
              <ThailandJobDemandPanel summary={summary} feeds={feeds} />
            </div>

            {isAgent && <ApplicantPipelinePanel pipeline={applicantPipeline} onSelect={setSelectedApplicationId} />}

            <div className="wx-dash-row wx-dash-row--wide-narrow wx-dash-row--top">
              <WorkerLineAttentionPanel feed={feeds[HIERARCHY_FEED_KEY]} />
              <TrendPanel updatedAt={updatedAt} feed={workerMetricFeed} />
            </div>
          </div>

          <div className="wx-dash-rail">
            <ScopeCard data={data} isAgent={isAgent} agentProfile={agentProfile} />
            <QuickActionsPanel isAgent={isAgent} isJobSideAgent={isJobSideAgent} />
            <WorkerLineSummaryPanel feed={feeds[HIERARCHY_FEED_KEY]} />
            <OperationalFeedsPanel feeds={feeds} />
          </div>
        </div>
      </div>

      {isAgent && (
        <DetailDrawer
          open={selectedApplicationId !== null}
          onClose={() => setSelectedApplicationId(null)}
          labelledBy={APPLICANT_DRAWER_TITLE_ID}
          selectableSelector=".wx-applicant-card"
        >
          <ApplicantDetailContent
            titleId={APPLICANT_DRAWER_TITLE_ID}
            state={applicantDetailState}
            onProfileSaved={applicantDetailState.refetch}
          />
        </DetailDrawer>
      )}
    </AppShell>
  );
}

/**
 * Picks the header CTA from whatever is genuinely open right now. Only ever
 * returns a route that already exists, and falls back to the War Room board
 * (never a filtered queue that would open empty) when no risk feed has
 * loaded a nonzero count yet.
 */
function resolvePrimaryAction(feeds, isAgent, isJobSideAgent) {
  const openCount = (feed) => (feed?.status === "ready" ? feed.totalCount ?? 0 : 0);

  // P156: every candidate CTA below opens a worker-safety queue. A job-side
  // Agent has no workers, so there is no honest primary action to offer -
  // return nothing rather than send them to a screen about other people's
  // workers, or invent a job-side CTA nobody asked for.
  if (isJobSideAgent) {
    return null;
  }

  if (isAgent) {
    // The Agent Portal keeps SOS and Abnormal on one combined two-tab page,
    // so both risk kinds resolve to the same real route for an Agent.
    if (openCount(feeds.sosOpen) > 0 || openCount(feeds.abnormalOpen) > 0 || openCount(feeds.heartbeatMissing) > 0) {
      return { label: "Open SOS / Abnormal", to: "/backoffice/agent/sos-abnormal" };
    }
    return { label: "Open SOS / Abnormal", to: "/backoffice/agent/sos-abnormal" };
  }

  if (openCount(feeds.sosOpen) > 0) {
    return { label: "Open SOS Cases", to: "/backoffice/sos/cases" };
  }
  if (openCount(feeds.abnormalOpen) > 0 || openCount(feeds.heartbeatMissing) > 0) {
    return { label: "Open Missing Heartbeat", to: "/backoffice/abnormal/heartbeat" };
  }
  return { label: "Open War Room", to: "/backoffice/war-room" };
}

/**
 * The right rail's compact identity/scope card - the dashboard's answer to
 * "whose data is this" without a dominant identity panel. SuperAdmin gets a
 * plain platform-wide scope line; an Agent gets their real agentCode plus
 * parent Super Agent/PLC names from the same profile fetch.
 */
function ScopeCard({ data, isAgent, agentProfile }) {
  return (
    <section className="wx-panel wx-rail-card wx-scope-card">
      <div className="wx-scope-card-head">
        <span className="wx-scope-card-name">{data.user.displayName}</span>
        <span className="wx-badge wx-badge--info">{data.primaryRole}</span>
      </div>
      {isAgent ? (
        agentProfile.status === "ready" && agentProfile.data ? (
          <dl className="wx-scope-card-detail">
            <div>
              <dt>Agent Code</dt>
              <dd>{agentProfile.data.agentCode}</dd>
            </div>
            <div>
              <dt>Super Agent</dt>
              <dd>{agentProfile.data.parentSuperAgent?.organizationName ?? "-"}</dd>
            </div>
            <div>
              <dt>PLC / บมจ</dt>
              <dd>{agentProfile.data.parentPlc?.organizationName ?? "-"}</dd>
            </div>
          </dl>
        ) : (
          <p className="wx-scope-card-note">{agentProfile.status === "error" ? "โหลด Scope ไม่สำเร็จ" : "กำลังโหลด Scope..."}</p>
        )
      ) : (
        <p className="wx-scope-card-note">ขอบเขต: ทุกสายงาน (Platform-wide)</p>
      )}
    </section>
  );
}

/**
 * One KPI tile. `attentionAccent` ("danger"/"warning") tints the entire
 * card - full border plus a soft surface wash, LiveMall's own attention
 * metric treatment - but only when the real value is genuinely above zero,
 * because an empty queue is not an attention signal. `healthyAccent` is the
 * mirror image for a metric whose nonzero value is good news. `to` links
 * the tile to the real list page that count came from; a tile with no exact
 * matching page (WORKERS is a derived combined total) stays a plain div
 * rather than link somewhere approximate.
 */
function MetricCard({ label, feed, valueSelector = (f) => f.totalCount, note, attentionAccent, healthyAccent, to }) {
  let valueNode;
  let accent = null;

  if (!feed || feed.status === "loading") {
    valueNode = <strong className="wx-dash-metric-value wx-dash-metric-value--loading">…</strong>;
  } else if (feed.status === "error") {
    valueNode = (
      <strong className="wx-dash-metric-value wx-dash-metric-value--error" title="โหลดข้อมูลไม่สำเร็จ กรุณาลองรีเฟรชหน้านี้อีกครั้ง">
        —
      </strong>
    );
  } else {
    const value = valueSelector(feed);
    const isPositive = typeof value === "number" && value > 0;
    if (attentionAccent && isPositive) {
      accent = attentionAccent;
    } else if (healthyAccent && isPositive) {
      accent = "success";
    }
    valueNode = <strong className={`wx-dash-metric-value${accent ? ` wx-dash-metric-value--${accent}` : ""}`}>{formatCount(value)}</strong>;
  }

  const className = `wx-dash-metric${accent ? ` wx-dash-metric--${accent}` : ""}${to ? " wx-dash-metric--link" : ""}`;
  const body = (
    <>
      <span className="wx-dash-metric-label">{label}</span>
      {valueNode}
      {note && <span className="wx-dash-metric-note">{note}</span>}
    </>
  );

  return to ? (
    <Link to={to} className={className}>
      {body}
    </Link>
  ) : (
    <div className={className}>{body}</div>
  );
}

// Accent follows the same green(normal)/amber(warning)/red(abnormal)/
// slate(neutral) vocabulary War Room's own seat coloring established for
// this exact set of signals. Active Geofence Zones is a coverage count, not
// a severity signal, so it stays neutral slate.
const OPERATIONS_SNAPSHOT_ITEMS = [
  { key: "sosOpen", label: "SOS", to: "/backoffice/sos/cases", accent: "danger" },
  { key: "abnormalOpen", label: "Abnormal", to: "/backoffice/abnormal/heartbeat", accent: "danger" },
  { key: "heartbeatMissing", label: "Missing Heartbeat", to: "/backoffice/tracking/latest", accent: "warning" },
  { key: "faceCheckAttention", label: "Face Check", to: "/backoffice/face-check/daily", accent: "warning" },
  { key: "fakeLocationHigh", label: "Fake Location", to: "/backoffice/tracking/fake-location", accent: "danger" },
  { key: "geofenceZonesActive", label: "Active Geofence", to: "/backoffice/geofence/zones", accent: "slate" },
];

/** The Safety Operations chart - a real plotted bar chart over the six live safety/compliance signals. */
function OperationsSnapshotPanel({ feeds }) {
  const bars = OPERATIONS_SNAPSHOT_ITEMS.map((item) => resolveBar(item.key, item.label, feeds[item.key], { accent: item.accent, to: item.to }));
  const openRisk = bars
    .filter((bar) => bar.accent !== "slate" && bar.status === "ready")
    .reduce((sum, bar) => sum + (bar.value ?? 0), 0);

  return (
    <ChartPanel
      title="Safety Operations"
      tag="Safety & compliance signals"
      footer={
        <>
          <span className="wx-chart-foot-stat">
            <strong>{formatCount(openRisk)}</strong> รายการเสี่ยงที่ยังเปิดอยู่
          </span>
          <Link to="/backoffice/war-room" className="wx-chart-foot-link">
            เปิด War Room
          </Link>
        </>
      }
    >
      <AxisBarChart bars={bars} emptyNote="ไม่มีสัญญาณด้านความปลอดภัยในขณะนี้" />
    </ChartPanel>
  );
}

/** Sums PLC/Agent/Worker/Invite counts for one Super Agent directly from the hierarchy tree - no invented numbers. */
function aggregateSuperAgent(superAgent) {
  const plcs = superAgent.plcs ?? [];
  let agentCount = 0;
  let workerCount = 0;
  let inviteCount = 0;

  for (const plc of plcs) {
    const agents = plc.agents ?? [];
    agentCount += agents.length;
    for (const agent of agents) {
      workerCount += agent.workerCount ?? 0;
      inviteCount += agent.inviteCount ?? 0;
    }
  }

  return { plcCount: plcs.length, agentCount, workerCount, inviteCount };
}

/** Grand total across every Super Agent row visible to the caller - the Worker Line Composition chart's own data source. */
function aggregateHierarchyTotals(superAgents) {
  let plcCount = 0;
  let agentCount = 0;
  let workerCount = 0;
  let inviteCount = 0;
  for (const superAgent of superAgents) {
    const totals = aggregateSuperAgent(superAgent);
    plcCount += totals.plcCount;
    agentCount += totals.agentCount;
    workerCount += totals.workerCount;
    inviteCount += totals.inviteCount;
  }
  return { superAgentCount: superAgents.length, plcCount, agentCount, workerCount, inviteCount };
}

/**
 * The Worker Line Composition flow (Super Agent -> PLC -> Agent -> Worker
 * -> Applications) - a grand-total companion to the per-line detail table.
 * Same hierarchy tree feed, no new backend call. "Applications" is the same
 * real inviteCount total the detail table already labels "Applications" per
 * Super Agent row. For an Agent session this feed is already scoped to
 * their own single branch.
 */
const WORKER_LINE_SUMMARY_ROWS = [
  { key: "superAgentCount", label: "Super Agent" },
  { key: "plcCount", label: "PLC / บมจ" },
  { key: "agentCount", label: "Agent" },
  { key: "workerCount", label: "Worker" },
  { key: "inviteCount", label: "Applications" },
];

function WorkerLineSummaryPanel({ feed }) {
  const status = !feed || feed.status === "loading" ? "loading" : feed.status === "error" ? "error" : "ready";
  const totals = status === "ready" ? aggregateHierarchyTotals(feed.data?.superAgents ?? []) : null;
  const nodes = WORKER_LINE_SUMMARY_ROWS.map((row) => ({
    key: row.key,
    label: row.label,
    status,
    value: totals ? totals[row.key] : null,
  }));

  return (
    <ChartPanel title="Worker Line Composition" tag="สายงาน" dense>
      <HierarchyFlow nodes={nodes} emptyNote="ยังไม่มีข้อมูลสรุปสายงานในระบบ" />
    </ChartPanel>
  );
}

function WorkerLineAttentionPanel({ feed }) {
  let body;
  if (!feed || feed.status === "loading") {
    body = <p className="wx-attention-empty">กำลังโหลดข้อมูลสายงาน...</p>;
  } else if (feed.status === "error") {
    body = <p className="wx-attention-empty">โหลดข้อมูลสายงานไม่สำเร็จ กรุณาลองรีเฟรชหน้านี้อีกครั้ง</p>;
  } else {
    const superAgents = feed.data?.superAgents ?? [];
    if (superAgents.length === 0) {
      body = <p className="wx-attention-empty">ยังไม่มีข้อมูลสายงานในระบบ</p>;
    } else {
      body = (
        <table className="wx-attention-table">
          <thead>
            <tr>
              <th>Line</th>
              <th>PLCs</th>
              <th>Agents</th>
              <th>Workers</th>
              <th>Applications</th>
            </tr>
          </thead>
          <tbody>
            {superAgents.map((superAgent) => {
              const totals = aggregateSuperAgent(superAgent);
              return (
                <tr key={superAgent.organizationId}>
                  <td>{superAgent.organizationName}</td>
                  <td className="wx-num">{formatCount(totals.plcCount)}</td>
                  <td className="wx-num">{formatCount(totals.agentCount)}</td>
                  <td className="wx-num">{formatCount(totals.workerCount)}</td>
                  <td className="wx-num">{formatCount(totals.inviteCount)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      );
    }
  }

  return (
    <section className="wx-panel wx-dash-panel">
      <div className="wx-dash-panel-head">
        <h2>Worker Line Attention</h2>
        <Link to="/backoffice/super-agents" className="wx-dash-panel-tag">
          ดูทั้งหมด
        </Link>
      </div>
      <div className="wx-attention-table-wrap">{body}</div>
    </section>
  );
}

/** The Worker Intake funnel - a real staged funnel with real conversion percentages. */
function WorkerIntakeFunnelPanel({ feeds, isAgent }) {
  // "Quick Registered" is always a real, traceable application count -
  // clicking it must reach the real record list, not just show a number.
  const quickRegisteredRoute = isAgent ? "/backoffice/agent/applications" : "/backoffice/worker-intake/invite-applications";
  const stages = [
    resolveBar("quickRegistered", "Quick Registered", feeds.applications, { to: quickRegisteredRoute }),
    resolveBar("ekycPending", "e-KYC Pending", feeds.ekyc, {
      to: "/backoffice/worker-intake/ekyc-review",
      selector: (f) => f.summary?.pendingReviewCount ?? f.totalCount,
    }),
    resolveBar("ekycVerified", "e-KYC Verified", feeds.ekyc, {
      selector: (f) => f.summary?.approvedCount ?? 0,
    }),
    resolveBar("workerProfiles", "Worker Profiles", feeds.workerProfiles, { to: "/backoffice/worker-intake/worker-profiles" }),
    resolveBar("readyForMatch", "Ready for Match", feeds.readyForMatchQueue, { to: "/backoffice/worker-intake/ready-for-match" }),
  ];

  const note = (
    <p className="wx-funnel-note">
      <strong>Quick register ยังไม่ใช่ ready for match</strong> — พนักงานต้องผ่านการยืนยัน e-KYC ก่อน จึงจะพร้อม match งานได้
    </p>
  );

  return (
    <ChartPanel title="Worker Intake Funnel" tag="ข้อมูลจริง" note={note}>
      <FunnelChart stages={stages} />
    </ChartPanel>
  );
}

/**
 * The Agent Dashboard's own glance-density attention panel: real Quick
 * Registered applicants as compact rows (name, live e-KYC status, submitted
 * time) so the intake process feels actionable without ever counting them
 * as a Worker Profile. The "ดูทั้งหมด" header link only renders on real
 * pagination overflow - every item shown already opens the shared drawer.
 */
function ApplicantPipelinePanel({ pipeline, onSelect }) {
  if (pipeline.status !== "ready" || pipeline.items.length === 0) {
    return null;
  }

  const hasMore = pipeline.totalCount > pipeline.items.length;

  return (
    <section className="wx-panel wx-dash-panel">
      <div className="wx-dash-panel-head">
        <h2>ผู้สมัครในสายงานที่รอ e-KYC</h2>
        {hasMore && (
          <Link to="/backoffice/agent/applications" className="wx-dash-panel-tag">
            ดูทั้งหมด ({formatCount(pipeline.totalCount)})
          </Link>
        )}
      </div>
      <div className="wx-dash-applicant-grid">
        {pipeline.items.map((item) => (
          <ApplicantDashboardItem key={item.applicationId} item={item} onClick={() => onSelect(item.applicationId)} />
        ))}
      </div>
    </section>
  );
}

/**
 * Worker Status / Compliance - the e-KYC Review Queue's own real summary
 * breakdown (EkycReviewSummaryDto), never an invented "compliance score".
 * Approved is a real success state and Rejected a real failure state, so
 * their green/red are meaningful status colors, not decoration.
 */
function ComplianceDistributionPanel({ feed }) {
  const segments = [
    resolveBar("pendingReview", "Pending Review", feed, { accent: "slate", selector: (f) => f.summary?.pendingReviewCount ?? 0 }),
    resolveBar("needsCorrection", "Needs Correction", feed, { accent: "warning", selector: (f) => f.summary?.needsCorrectionCount ?? 0 }),
    resolveBar("approved", "Approved", feed, { accent: "success", selector: (f) => f.summary?.approvedCount ?? 0 }),
    resolveBar("rejected", "Rejected", feed, { accent: "danger", selector: (f) => f.summary?.rejectedCount ?? 0 }),
  ];
  const allZero = segments.every((s) => s.status === "ready") && segments.every((s) => (s.value ?? 0) === 0);

  return (
    <ChartPanel title="Worker Status / Compliance" tag="e-KYC review outcomes">
      <DonutChart segments={segments} centerLabel="รวม e-KYC" />
      {allZero && <p className="wx-chart-empty-note">ยังไม่มีใบสมัครเข้าสู่ขั้นตอน e-KYC</p>}
    </ChartPanel>
  );
}

/** The Job Demand chart (Employers, Jobs, Open Jobs) plus a compact stat row for the pipeline's next stages. */
function ThailandJobDemandPanel({ summary, feeds }) {
  const bars = [
    { key: "employers", label: "Employers", status: "ready", value: summary.visibleEmployerCount, accent: "slate", to: "/backoffice/thailand-jobs/employers" },
    resolveBar("jobs", "Jobs", feeds.jobsTotal, { accent: "primary", to: "/backoffice/thailand-jobs/jobs" }),
    { key: "openJobs", label: "Open Jobs", status: "ready", value: summary.visibleJobCount, accent: "primary-strong", to: "/backoffice/thailand-jobs/jobs" },
  ];

  const miniStats = [
    resolveBar("jobsReadyForMatching", "Ready for Matching", feeds.jobsReadyForMatching),
    resolveBar("matchingQueue", "Matching Queue", feeds.matchingQueueJobs),
    resolveBar("activeAssignments", "Active Assignments", feeds.assignmentsActive),
  ];

  return (
    <ChartPanel title="Thailand Job Demand" tag="นายจ้าง → งาน → แรงงานที่ต้องการ">
      <AxisBarChart bars={bars} emptyNote="ยังไม่มีข้อมูล Job Demand ในขอบเขตนี้" />
      <div className="wx-mini-stat-row">
        {miniStats.map((stat) => (
          <div key={stat.key} className="wx-mini-stat">
            <strong>{stat.status === "ready" ? formatCount(stat.value) : stat.status === "loading" ? "…" : "—"}</strong>
            <span>{stat.label}</span>
          </div>
        ))}
      </div>
    </ChartPanel>
  );
}

/**
 * The Trend slot. This backend exposes no time-series endpoint anywhere
 * (every dashboard feed is a point-in-time totalCount/summary), so there is
 * no real history to plot. Rather than fabricate a slope, this renders a
 * real gridded chart frame whose single plotted mark is the one day that
 * genuinely exists - today - carrying a real current value from an
 * already-fetched feed.
 */
function TrendPanel({ updatedAt, feed }) {
  const todayLabel = updatedAt.toLocaleDateString("th-TH-u-ca-gregory", { day: "2-digit", month: "short", year: "numeric" });
  const todayValue = feed?.status === "ready" ? formatCount(feed.totalCount) : feed?.status === "error" ? "—" : "…";

  return (
    <ChartPanel title="Trend" tag="ข้อมูลย้อนหลัง">
      <TrendShell todayLabel={todayLabel} todayValue={todayValue} valueLabel="Workers" />
    </ChartPanel>
  );
}

// Grouped into 4 real domains rather than one row per feed route - that
// read as a cramped debug list, not a dashboard panel. Each tile's state is
// a real aggregate of its member feeds' own client fetch status (never a
// fabricated backend health signal).
const FEED_CATEGORIES = [
  { key: "workerIntake", label: "Worker Intake", feedKeys: ["applications", "ekyc", "workerProfiles", "readyForMatchQueue"] },
  { key: "jobDemand", label: "Job Demand", feedKeys: ["jobsTotal", "jobsReadyForMatching", "matchingQueueJobs", "assignmentsActive"] },
  {
    key: "safety",
    label: "Safety & Compliance",
    feedKeys: ["sosOpen", "abnormalOpen", "heartbeatMissing", "fakeLocationHigh", "faceCheckAttention", "geofenceZonesActive"],
  },
  { key: "hierarchy", label: "Worker Line Data", feedKeys: [HIERARCHY_FEED_KEY] },
];

function categoryState(feeds, feedKeys) {
  const statuses = feedKeys.map((key) => feeds[key]?.status ?? "loading");
  if (statuses.some((status) => status === "error")) {
    return "failed";
  }
  if (statuses.some((status) => status !== "ready")) {
    return "loading";
  }
  return "loaded";
}

/** Compact status matrix - secondary in weight but a real, honest connectivity signal for every feed this dashboard depends on. */
function OperationalFeedsPanel({ feeds }) {
  const tiles = FEED_CATEGORIES.map((category) => ({
    key: category.key,
    label: category.label,
    state: categoryState(feeds, category.feedKeys),
  }));
  const healthyCount = tiles.filter((tile) => tile.state === "loaded").length;

  return (
    <section className="wx-panel wx-rail-card wx-chart-panel">
      <div className="wx-dash-panel-head">
        <h2>Operational Feeds</h2>
        <span className="wx-dash-panel-tag">
          {healthyCount}/{tiles.length} ปกติ
        </span>
      </div>
      <div className="wx-feeds-tiles">
        {tiles.map((tile) => (
          <div key={tile.key} className={`wx-feeds-tile wx-feeds-tile--${tile.state}`}>
            <span className="wx-feeds-tile-dot" aria-hidden="true" />
            <span className="wx-feeds-tile-label">{tile.label}</span>
            <span className="wx-feeds-tile-state">{FEED_STATE_LABELS[tile.state]}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

/** Lives in the right rail - a compact vertical action list, never a wide block competing with the analytics grid. */
function QuickActionsPanel({ isAgent, isJobSideAgent }) {
  const actions = isJobSideAgent
    ? AGENT_JOB_SIDE_QUICK_ACTIONS
    : isAgent
      ? AGENT_QUICK_ACTIONS
      : SUPERADMIN_QUICK_ACTIONS;
  const title = isAgent ? "Quick Actions" : "Quick SuperAdmin Actions";

  return (
    <section className="wx-panel wx-rail-card">
      <div className="wx-dash-panel-head">
        <h2>{title}</h2>
        <span className="wx-dash-panel-tag">ตามสิทธิ์ของคุณ</span>
      </div>
      <div className="wx-dash-actions wx-dash-actions--rail">
        {actions.map((action) => (
          <Link key={action.label} to={action.to} className="wx-dash-action-btn wx-dash-action-btn--rail">
            {action.label}
          </Link>
        ))}
      </div>
    </section>
  );
}
